import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  SecurityContext,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { PandaDocSessionResponse } from '@core/dto/start-now-app.dto';
import { FormErrorTypes } from '@core/enums/form-error-type.enum';
import { Locale } from '@core/enums/locale';
import { StartNowAppService } from '@core/services/start-now-app.service';
import { AppState } from '@core/store';
import {
  selectAgreementStep,
  selectPandaDocumentId,
  selectStartNowAppData,
  selectUserInfo,
} from '@core/store/start-now-app';
import {
  AgreementStep,
  StartNowAppData,
} from '@core/store/start-now-app/start-now-app-state-models';
import {
  resetAgreementStep,
  stepProcessing,
  updateAgreementStepNavigableFlag,
  updatePandaDocumentId,
  updateSsnNumber,
} from '@core/store/start-now-app/start-now-app.actions';
import { environment } from '@env';
import { Store, select } from '@ngrx/store';
import { SafeResourceUrlPipe } from '@shared/pipes/safe-url.pipe';
import { saveFile } from '@shared/utils/save-file.utils';
import { getFormValidationErrorMessage } from '@shared/utils/validation.utils';
import { StatusCodes } from 'http-status-codes';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { StartNowStepBaseComponent } from '../../start-now-app-step-base/start-now-step-base.component';

@Component({
  selector: 'app-consultant-agreement-step',
  templateUrl: './consultant-agreement-step.component.html',
  styleUrls: ['./consultant-agreement-step.component.scss'],
})
export class ConsultantAgreementStepComponent
  extends StartNowStepBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  termsConditionsURL: string = '';
  pandaDocUrl: string | null = null;
  pandaDocSafeUrl: string;
  loading: boolean = false;
  documentCompleted: boolean = false;
  documentNotSignedError: boolean = false;
  pandaDocError: boolean = false;
  pandaDocDocumentId: string = null;
  startNowAppData: StartNowAppData;
  readonly SsnNumberFieldName: string = 'ssnNumber';
  readonly StatusCodes = StatusCodes;
  readonly SsnErrorMessage: string = $localize`Please enter a valid Social Security Number`;
  readonly ErrorMessages = {
    [FormErrorTypes.required]: $localize`You need to agree before you got to the next step!`,
  };

  getFormValidationErrorMessage = getFormValidationErrorMessage;

  public agreementFormGroup: FormGroup;
  public ssnNumberFormGroup: FormGroup;
  pandaDocumentId$: Observable<string>;
  termsConditionsSafeUrl: string;
  agreementStep$: Observable<AgreementStep>;

  constructor(
    private store$: Store<AppState>,
    private fb: FormBuilder,
    private safeResourceUrlPipe: SafeResourceUrlPipe,
    private domSanitizer: DomSanitizer,
    private startNowAppService: StartNowAppService,
    @Inject(LOCALE_ID) private localeId: Locale,
    injector: Injector,
  ) {
    super(injector, 'SNA Step - 7 Consultant Agreement');
  }
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.store$.dispatch(stepProcessing({ stepProcessing: false }));
    }, 500);
  }

  ngOnInit(): void {
    this.agreementStep$ = this.store$.select(selectAgreementStep);
    this.termsConditionsURL = `${environment.agreementHtmlBaseUrl}/agreements/consultantAgreement-${
      this.localeId === 'en' ? 'en-US' : this.localeId
    }.html`;
    this.agreementFormGroup = this.createFormGroup();
    this.pandaDocumentId$ = this.store$.select(selectPandaDocumentId);
    //this.listenIsNextEnabled();
    this.requestStartNowAppData();

    if (this.isUsaEnv) {
      this.ssnNumberFormGroup = this.createSsnFormGroup();
      this.prePopulateSsnNumberForm();
    }
    this.subscriptions.add(
      this.pandaDocumentId$.subscribe((value) => {
        this.pandaDocDocumentId = value;
        console.log('PandaDoc Document ID:', this.pandaDocDocumentId);
      }),
    );
    this.termsConditionsSafeUrl = this.getAgreementSafeUrl();
    if (this.pandaDocDocumentId != null) {
      this.documentCompleted = true;
      this.listenIsNextEnabled();
      //this.store$.dispatch(updateAgreementStepNavigableFlag({ isNextEnabled: true }));
    } else {
      this.createDocumentAndOpen();
      this.pandaDocSafeUrl = this.getPandaDocSafeUrl();
      console.log('panda doc url:', this.pandaDocUrl);
      console.log('panda doc url:', this.pandaDocSafeUrl);
      this.store$.dispatch(updateAgreementStepNavigableFlag({ isNextEnabled: false }));
      window.addEventListener('message', this.handlePandaDocMessage);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store$.dispatch(resetAgreementStep());
    window.removeEventListener('message', this.handlePandaDocMessage);
  }

  submitStep(): void {
    // Prevent proceeding until PandaDoc confirms the agreement is signed.
    if (!this.documentCompleted) {
      this.documentNotSignedError = true;
      return;
    }

    this.documentNotSignedError = false;

    if (this.isUsaEnv) {
      if (this.ssnNumberFormGroup.valid) {
        this.store$.dispatch(
          updateSsnNumber({
            ssnNumber: this.ssnNumberFormGroup.get(this.SsnNumberFieldName).value,
          }),
        );
      }
    } else {
      this.store$.dispatch(
        updateAgreementStepNavigableFlag({
          isNextEnabled: true,
        }),
      );
    }
  }

  getAgreementSafeUrl(): string {
    return this.domSanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      this.safeResourceUrlPipe.transform(this.termsConditionsURL),
    );
  }

  createDocumentAndOpen(): void {
    const email = this.startNowAppData.contactInfo.email;
    const firstName = this.startNowAppData.userInfo.firstName;
    const lastName = this.startNowAppData.userInfo.lastName;

    this.loading = true;
    console.log('local Id: ', this.localeId);
    this.startNowAppService
      .createPDDocumentAndSession(
        email,
        firstName,
        lastName,
        this.localeId === 'en-US' || this.localeId === 'en' ? 'en-US' : 'en-MX',
      )
      .subscribe({
        next: (response: PandaDocSessionResponse) => {
          console.log('PandaDoc session created:', response);

          this.openPandaDoc(response.id);
        },

        error: (error) => {
          console.error('PandaDoc error:', error);

          this.loading = false;
        },
      });
  }

  getPandaDocSafeUrl(): string {
    return this.domSanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      this.safeResourceUrlPipe.transform(this.pandaDocUrl),
    );
  }

  public printAgreement() {
    this.startNowAppService.getAgreementPdf().subscribe((blob) => {
      saveFile(blob, $localize`ConsultantAgreement.pdf`, 'application/pdf');
    });
  }

  protected createFormGroup(): FormGroup {
    return this.fb.group({});
  }

  private requestStartNowAppData() {
    this.subscriptions.add(
      this.store$
        .select(selectStartNowAppData)
        .pipe(
          filter((startNowAppData) => !!startNowAppData),
          take(1),
        )
        .subscribe((startNowAppData) => {
          this.startNowAppData = startNowAppData;
        }),
    );
  }

  private handlePandaDocMessage = (event: MessageEvent): void => {
    if (event.origin !== 'https://app.pandadoc.com') {
      return;
    }

    console.log('PandaDoc event:', event.data?.type);

    if (event.data?.type === 'session_view.document.completed') {
      this.documentCompleted = true;
      this.documentNotSignedError = false;
      console.log('Completed event payload:', event.data?.payload);
      console.log('Completed event payload1:', event.data?.payload.uuid);
      this.pandaDocDocumentId = event.data?.payload.uuid;
      this.store$.dispatch(
        updatePandaDocumentId({
          pandaDocumentId: this.pandaDocDocumentId,
        }),
      );
      //this.startNowAppData.pandaDocumentId = event.data?.uuid;
      this.listenIsNextEnabled();
    } else if (
      event.data?.type.toLowerCase().includes('error') ||
      event.data?.type.toLowerCase().includes('failed')
    ) {
      this.pandaDocError = true;
    }
  };

  private openPandaDoc(sessionId: string): void {
    this.pandaDocUrl = `https://app.pandadoc.com/s/${sessionId}`;
    console.log('panda doc url:', this.pandaDocUrl);
    this.loading = false;
  }

  private createSsnFormGroup(): FormGroup {
    return this.fb.group({
      [this.SsnNumberFieldName]: ['', [Validators.minLength(11)]],
    });
  }

  private nextStep(): void {
    this.store$.dispatch(
      updatePandaDocumentId({
        pandaDocumentId: this.pandaDocDocumentId,
      }),
    );
    this.goToNextStep.emit();
  }

  private listenIsNextEnabled(): void {
    this.subscriptions.add(
      this.agreementStep$
        .pipe(
          filter(
            (agreementStep) =>
              agreementStep?.isNextEnabled &&
              !agreementStep?.errors?.length &&
              !agreementStep?.statusCode,
          ),
          take(1),
        )
        .subscribe(() => this.nextStep()),
    );
  }

  private prePopulateSsnNumberForm(): void {
    this.store$
      .select(selectUserInfo)
      .pipe(
        select((userInfo) => userInfo?.ssnNumber),
        take(1),
        filter((ssnNumber) => !!ssnNumber),
      )
      .subscribe((ssnNumber) => {
        this.ssnNumberFormGroup.patchValue({ [this.SsnNumberFieldName]: ssnNumber });
      });
  }
}
